import { useState } from 'react';
import type { TableProps } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';

import { DataTable } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayGioVN } from '../../../lib/dinh-dang';
import { BADGE_BAN_GHI, BADGE_MUC } from '../constants';
import { useDanhSachTacVu, useNhatKy } from '../hooks/useCanhBao';
import { useDichCanhBao } from '../hooks/useDichCanhBao';
import type { LocBanGhi, NotifyRecord, PhamViCanhBao, TrangThaiBanGhi } from '../types';
import { NganBanGhi } from './NganBanGhi';
import { VungLoi } from './VungLoi';

type PhamViLoc = 'tat-ca' | 'nen-tang' | 'mot-tenant';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Nhật ký cảnh báo (aaPanel: Alarm Log): mỗi lần tác vụ kích hoạt một dòng, kể cả lần bị giới hạn gửi chặn. Bấm một
 * dòng mở ngăn chi tiết: nội dung, từng lần gửi theo kênh, dòng thời gian các lần thử, nút gửi lại.
 *
 * `giamSat` là bản của tab "Giám sát gửi" ở cổng nền tảng: thêm cột phạm vi và bộ lọc theo tenant.
 */
export function NhatKyCanhBao({
  pv,
  taskId,
  onDoiTask,
  giamSat = false,
  duocSua,
}: {
  pv: PhamViCanhBao;
  taskId?: string;
  onDoiTask?: (taskId: string | undefined) => void;
  giamSat?: boolean;
  duocSua: boolean;
}) {
  const { t, locale } = useT();
  const dich = useDichCanhBao();
  const dichLoi = useLoi();
  const [trangThai, setTrangThai] = useState<TrangThaiBanGhi | undefined>();
  const [phamVi, setPhamVi] = useState<PhamViLoc>('tat-ca');
  const [maTenant, setMaTenant] = useState('');
  const [chiTiet, setChiTiet] = useState<string | null>(null);
  const tacVu = useDanhSachTacVu(pv);

  const tenantHopLe = UUID.test(maTenant.trim());
  const loc: LocBanGhi = giamSat
    ? {
        status: trangThai,
        platform_only: phamVi === 'nen-tang' || undefined,
        tenant_id: phamVi === 'mot-tenant' && tenantHopLe ? maTenant.trim() : undefined,
      }
    : {
        status: trangThai,
        task_id: taskId,
        platform_only: pv === 'nen-tang' || undefined,
      };
  const q = useNhatKy(pv, loc);
  const dong = q.data?.pages.flatMap((p) => p.items) ?? [];
  const coLoc = Boolean(trangThai || (!giamSat && taskId) || (giamSat && phamVi !== 'tat-ca'));
  const dangTai = q.isLoading;

  const columns: TableProps<NotifyRecord>['columns'] = [
    {
      title: t('canhBaoNhatKy.cotThoiGian'),
      width: 150,
      render: (_v, row) => (
        <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">
          {ngayGioVN(row.created_at, locale)}
        </span>
      ),
    },
    ...(giamSat
      ? [
          {
            title: t('canhBaoNhatKy.cotTenant'),
            width: 150,
            render: (_v: unknown, row: NotifyRecord) =>
              row.tenant_id ? (
                <span
                  className="block max-w-[9rem] truncate font-mono text-2xs text-content-muted"
                  title={row.tenant_id}
                >
                  {row.tenant_id}
                </span>
              ) : (
                <Badge variant="primary">{t('canhBaoNhatKy.nenTang')}</Badge>
              ),
          },
        ]
      : []),
    {
      title: t('canhBaoNhatKy.cotNoiDung'),
      render: (_v, row) => (
        <div className="flex min-w-0 max-w-md flex-col">
          <Button
            variant="link"
            size="sm"
            className="min-w-0 justify-start"
            aria-label={t('canhBaoNhatKy.xemChiTiet', { ten: row.title })}
            onClick={() => setChiTiet(row.id)}
          >
            <span className="truncate">{row.title}</span>
          </Button>
          <span className="truncate text-2xs text-content-muted">
            {row.task_title ?? t('canhBaoNhatKy.tacVuDaXoa')} · {dich.tenLoai(row.type_code)}
          </span>
        </div>
      ),
    },
    {
      title: t('canhBaoNhatKy.cotMuc'),
      width: 120,
      render: (_v, row) => <Badge variant={BADGE_MUC[row.severity].variant}>{t(BADGE_MUC[row.severity].khoa)}</Badge>,
    },
    {
      title: t('canhBaoNhatKy.cotTrangThai'),
      width: 220,
      render: (_v, row) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <Badge variant={BADGE_BAN_GHI[row.status].variant}>{t(BADGE_BAN_GHI[row.status].khoa)}</Badge>
          {row.status === 'SUPPRESSED' && row.suppressed_reason ? (
            <span className="text-2xs text-content-muted">{dich.lyDo(row.suppressed_reason)}</span>
          ) : row.deliveries_total > 0 ? (
            <span className="text-2xs text-content-muted">
              {t('canhBaoNhatKy.soKenh', {
                dat: row.deliveries_succeeded,
                tong: row.deliveries_total,
              })}
            </span>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
        {giamSat ? (
          <>
            <Select<PhamViLoc>
              className="w-full sm:w-56"
              aria-label={t('canhBaoNenTang.locPhamVi')}
              value={phamVi}
              onChange={setPhamVi}
              options={[
                { value: 'tat-ca', label: t('canhBaoNenTang.moiPhamVi') },
                { value: 'nen-tang', label: t('canhBaoNenTang.chiNenTang') },
                { value: 'mot-tenant', label: t('canhBaoNenTang.motTenant') },
              ]}
            />
            {phamVi === 'mot-tenant' && (
              <Input
                className="w-full font-mono sm:w-80"
                allowClear
                placeholder={t('canhBaoNenTang.maTenant')}
                aria-label={t('canhBaoNenTang.maTenant')}
                status={maTenant.trim() && !tenantHopLe ? 'error' : undefined}
                title={maTenant.trim() && !tenantHopLe ? t('canhBaoNenTang.maTenantSai') : undefined}
                value={maTenant}
                onChange={(e) => setMaTenant(e.target.value)}
              />
            )}
          </>
        ) : (
          <Select<string>
            className="w-full sm:w-64"
            aria-label={t('canhBaoNhatKy.locTacVu')}
            value={taskId ?? ''}
            onChange={(v) => onDoiTask?.(v || undefined)}
            options={[
              { value: '', label: t('canhBaoNhatKy.moiTacVu') },
              ...(tacVu.data ?? []).map((tv) => ({
                value: tv.id,
                label: tv.title,
              })),
            ]}
          />
        )}
        <Select<TrangThaiBanGhi | ''>
          className="w-full sm:w-48"
          aria-label={t('canhBaoNhatKy.locTrangThai')}
          value={trangThai ?? ''}
          onChange={(v) => setTrangThai(v || undefined)}
          options={[
            { value: '', label: t('canhBaoNhatKy.moiTrangThai') },
            ...(Object.keys(BADGE_BAN_GHI) as TrangThaiBanGhi[]).map((s) => ({
              value: s,
              label: t(BADGE_BAN_GHI[s].khoa),
            })),
          ]}
        />
        {giamSat && phamVi === 'mot-tenant' && maTenant.trim() !== '' && !tenantHopLe && (
          <p className="w-full text-xs text-danger-600">{t('canhBaoNenTang.maTenantSai')}</p>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
        {q.error && !q.data ? (
          <VungLoi cau={t('canhBaoNhatKy.loi', { loi: dichLoi(q.error) })} onThuLai={() => void q.refetch()} />
        ) : (
          <>
            <DataTable<NotifyRecord>
              caption={t('canhBaoNhatKy.bang')}
              rowKey="id"
              dataSource={dong}
              columns={columns}
              loading={dangTai}
              size="middle"
              pagination={false}
              locale={{
                emptyText: dangTai ? (
                  ' '
                ) : (
                  <Empty
                    icon={HistoryOutlined}
                    // Rỗng vì bộ lọc: bỏ mô tả.
                    title={coLoc ? t('canhBaoNhatKy.rongLoc') : t('canhBaoNhatKy.rong')}
                    description={coLoc ? undefined : t('canhBaoNhatKy.rongMo')}
                  />
                ),
              }}
            />
            {q.hasNextPage && (
              <div className="flex justify-center border-t border-default px-4 py-3">
                <Button
                  variant="outline"
                  size="sm"
                  loading={q.isFetchingNextPage}
                  onClick={() => void q.fetchNextPage()}
                >
                  {t('canhBaoNhatKy.taiThem')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <NganBanGhi pv={pv} id={chiTiet} duocSua={duocSua} onClose={() => setChiTiet(null)} />
    </div>
  );
}
